import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus, RefreshCw } from 'lucide-react';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import { Avatar, Field, IdChip, ListCard, Modal, RowActions, Table, inputCls, rowCls, tdCls } from '../components/ui';

const Sanctions = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/type-sanction');
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter((i) => (i.libelle_sanction || '').toLowerCase().includes(q)) : data;
  }, [data, search]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce type de sanction ?')) return;
    try {
      await http.delete(`/api/type-sanction/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) await http.put(`/api/type-sanction/${editingItem.idtype_sanction}`, formData);
      else await http.post('/api/type-sanction', formData);
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  return (
    <div className="min-h-full bg-slate-50 dark:bg-slate-950">
      <BlueHeader
        title="Sanctions"
        subtitle="Gestion des sanctions disciplinaires"
        icon={AlertTriangle}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
        ]}
      />

      <div className="p-6">
        <ListCard
          count={filtered.length} singular="sanction affichée" plural="sanctions affichées"
          search={search} onSearch={setSearch} placeholder="Rechercher une sanction"
          loading={loading} hasData={data.length > 0} icon={AlertTriangle}
          emptyTitle="Aucune sanction pour le moment" emptyText="Créez votre premier type de sanction pour commencer."
          onCreate={handleCreate} createLabel="Créer une sanction" createIcon={Plus}
        >
          <Table heads={['Sanction', 'Actions']}>
            {filtered.map((item) => (
              <tr key={item.idtype_sanction} className={rowCls}>
                <td className={tdCls}>
                  <div className="flex items-center gap-3">
                    <Avatar icon={AlertTriangle} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.libelle_sanction}</p>
                      <p className="mt-0.5"><IdChip>#{item.idtype_sanction}</IdChip></p>
                    </div>
                  </div>
                </td>
                <td className={tdCls}>
                  <RowActions onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item.idtype_sanction)} />
                </td>
              </tr>
            ))}
          </Table>
        </ListCard>
      </div>

      {showModal && (
        <Modal
          title={editingItem ? 'Modifier la sanction' : 'Créer une sanction'}
          subtitle={editingItem ? editingItem.libelle_sanction : 'Renseignez le type de sanction'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          confirmLabel={editingItem ? 'Enregistrer' : 'Créer'}
        >
          <div className="space-y-5 p-6">
            <Field label="Libellé de la sanction">
              <input
                type="text"
                className={inputCls}
                value={formData.libelle_sanction || ''}
                onChange={(e) => setFormData({ ...formData, libelle_sanction: e.target.value })}
                required
              />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Sanctions;